'use client';

import { useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { Button } from '@//components/Button';
import { Gallery } from '@//components/Gallery';
import { useLegacyTrackImagesFullscreenClick } from '@//data/tracking/useLegacyTrackImagesFullscreenClick';
import useDominantColor from '@//hooks/useDominantColor';
import {
  type CfitImage,
  type CfitOptionsWidth,
  extractCacheBustingParam,
  urlToCfit,
} from '@//utils/image/urlToCfit';
import styles from './MediaGallery.module.scss';

// Modal subtree (GalleryModal + GalleryModal360 + GalleryModalSphere +
// GalleryModalImage + GalleryModalVideo and their CSS) is only needed once the
// user opens the gallery. Lazy-loading keeps ~40+ unused rules out of the
// initial item-page CSS chunk.
const GalleryModal = lazy(() => import('./GalleryModal'));

interface ResponsiveSource {
  src: string;
  srcSet?: string;
  media?: string;
  type?: string;
}

const generateResponsiveSources = (
  url: string,
  alt: string,
  r?: string | null,
): ResponsiveSource[] => {
  const sources: ResponsiveSource[] = [];

  // Generate WebP sources for all three sizes
  const webpSmall = urlToCfit(url, {
    width: 600 as CfitOptionsWidth,
    alt,
    format: 'webp',
    r,
  });

  const webpMedium = urlToCfit(url, {
    width: 840 as CfitOptionsWidth,
    alt,
    format: 'webp',
    r,
  });

  const webpLarge = urlToCfit(url, {
    width: 1200 as CfitOptionsWidth,
    alt,
    format: 'webp',
    r,
  });

  // Create srcset for WebP with all three sizes
  const webpSrcSet = `${webpSmall.src} 600w, ${webpMedium.src} 840w, ${webpLarge.src} 1200w`;

  sources.push({
    src: webpSrcSet,
    srcSet: webpSrcSet,
    type: 'image/webp',
  });

  // Generate JPEG sources for all three sizes
  const jpegSmall = urlToCfit(url, {
    width: 600 as CfitOptionsWidth,
    alt,
    format: 'jpeg',
    r,
  });

  const jpegMedium = urlToCfit(url, {
    width: 840 as CfitOptionsWidth,
    alt,
    format: 'jpeg',
    r,
  });

  const jpegLarge = urlToCfit(url, {
    width: 1200 as CfitOptionsWidth,
    alt,
    format: 'jpeg',
    r,
  });

  // Create srcset for JPEG with all three sizes
  const jpegSrcSet = `${jpegSmall.src} 600w, ${jpegMedium.src} 840w, ${jpegLarge.src} 1200w`;

  sources.push({
    src: jpegSrcSet,
    srcSet: jpegSrcSet,
    type: 'image/jpeg',
  });

  return sources;
};

interface MediaGalleryProps {
  vehicle: ElasticVehicle;
}

export const MediaGallery: React.FC<MediaGalleryProps> = ({ vehicle }) => {
  const t = useTranslations('pages.item.imageGallery');
  const { trackLegacyImagesFullscreenClick } = useLegacyTrackImagesFullscreenClick();
  const [galleryModal, setGalleryModal] = useState(false);
  // Tracks whether the lazy GalleryModal chunk has been requested yet. Once
  // true we keep the component mounted so its exit transition can play on
  // subsequent close/open cycles without re-fetching the chunk.
  const [galleryModalMounted, setGalleryModalMounted] = useState(false);
  const vehicleTitle = `${vehicle.title} ${vehicle.shortDescription}`;

  const onOpenGallery = useCallback(() => {
    setGalleryModalMounted(true);
    setGalleryModal(true);
    trackLegacyImagesFullscreenClick(vehicle);
  }, [vehicle, trackLegacyImagesFullscreenClick]);
  const onOpenGalleryFromButton = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenGallery();
    },
    [onOpenGallery],
  );
  const onCloseGallery = useCallback(() => setGalleryModal(false), []);

  const assetPrefix = process.env.NEXT_PUBLIC_ASSETS_CDN || '';

  const fallbackImage: CfitImage = {
    src: `${assetPrefix}/images/placeholders/product-gallery.jpg`,
    srcSet: `${assetPrefix}/images/placeholders/product-gallery@2x.jpg 2x`,
    alt: t('fallbackImageAlt'),
    width: 450,
    height: 299,
  };

  // Get the first image URL for dominant color extraction
  const firstImageUrl = useMemo(() => {
    const firstImageMedia = vehicle.media?.find((media) => media.type === 'Image');
    return firstImageMedia?.files?.[0]?.url;
  }, [vehicle.media]);

  // Extract dominant color from the first image
  const { dominantColor } = useDominantColor(firstImageUrl);

  const sizesFirst =
    '(min-width: 2001px) 956px, (min-width: 896px and max-width: 2000px) calc(50vw - 80px - 4px), 100vw';
  const sizesOtherHalf =
    '(min-width: 2001px) 956px, (min-width: 896px and max-width: 2000px) calc(50vw - 80px - 4px), 0vw';
  const sizesOtherThird =
    '(min-width: 2001px) 474px, (min-width: 896px and max-width: 2000px) calc(25vw - 80px - 8px), 0vw';
  const sizesOtherQuarter =
    '(min-width: 2001px) 474px, (min-width: 896px and max-width: 2000px) calc(25vw - 80px - 8px), 0vw';

  return (
    <>
      {galleryModalMounted && (
        <Suspense fallback={null}>
          <GalleryModal
            isOpen={galleryModal}
            onClose={onCloseGallery}
            media={vehicle.media}
            vehicleTitle={vehicleTitle}
          />
        </Suspense>
      )}
      <Gallery
        ariaLabel={t('label')}
        onClick={vehicle.media?.length ? onOpenGallery : undefined}
        images={
          vehicle.media
            ?.filter((media) => media.type === 'Image')
            .slice(0, 5)
            .map((media, index) => {
              if (!media.files?.[0]?.url) {
                return {
                  src: fallbackImage.src,
                  srcSet: fallbackImage.srcSet,
                  alt: fallbackImage.alt,
                  width: fallbackImage.width,
                  height: fallbackImage.height,
                };
              }

              const r = extractCacheBustingParam(media.files[0].formats);

              const alt = t('imageAlt', {
                count: index + 1,
                name: vehicleTitle,
              });

              const sources = generateResponsiveSources(media.files[0].url, alt, r);

              // Use medium size (840) as fallback
              const fallbackImg = urlToCfit(media.files[0].url, {
                width: 840 as CfitOptionsWidth,
                alt,
                format: 'jpeg',
                r,
              });

              // Determine the correct sizes attribute based on position and total count
              const totalImages = vehicle.media?.filter((m) => m.type === 'Image')?.length ?? 0;
              let sizes: string;

              if (index === 0) {
                // First image is ALWAYS sizesFirst
                sizes = sizesFirst;
              } else if (index === 1) {
                // Second image logic
                if (totalImages === 2) {
                  sizes = sizesOtherHalf;
                } else if (totalImages === 3) {
                  sizes = sizesOtherThird;
                } else {
                  sizes = sizesOtherQuarter;
                }
              } else if (index === 2) {
                // Third image logic
                if (totalImages === 3) {
                  sizes = sizesOtherThird;
                } else {
                  sizes = sizesOtherQuarter;
                }
              } else {
                // All other images (4th, 5th, etc.)
                sizes = sizesOtherQuarter;
              }

              return {
                src: fallbackImg.src,
                srcSet: fallbackImg.srcSet,
                sources,
                alt,
                sizes,
                width: 840,
                height: 630,
              };
            }) ?? [
            {
              src: fallbackImage.src,
              srcSet: fallbackImage.srcSet,
              alt: fallbackImage.alt,
              width: fallbackImage.width,
              height: fallbackImage.height,
            },
          ]
        }
        action={
          vehicle.media?.length ? (
            <Button
              variant="inverted"
              size="md"
              onClick={onOpenGalleryFromButton}
              title={t('showNumberOfMedia', { count: vehicle.media?.length })}
              data-testid="show-all-media-button"
            >
              {t('showNumberOfMedia', { count: vehicle.media?.length })}
            </Button>
          ) : undefined
        }
        testId="image-gallery"
        className={styles.gallery}
        dominantColor={dominantColor}
      />
    </>
  );
};
